import React, { useEffect, useState } from "react";
import { customFetchV2 } from "../../../services/custom-fetch-v2";
import Loading from "../Loading.jsx";
import SectionHeader from "./SectionHeader";
import Button from "./Button.tsx";

interface Subscription {
  deviceModel: string;
  deviceImei: string;
  planName: string;
}

interface SubscriptionRowProps {
  subscription: Subscription;
}

type GetSubscriptionsResponse = Subscription[];

const SubscriptionRow: React.FC<SubscriptionRowProps> = ({ subscription }) => {
  const { deviceModel, deviceImei, planName } = subscription;

  return (
    <tr>
      <td>{deviceModel}</td>
      <td>{deviceImei}</td>
      <td className="is-hidden-mobile">{planName}</td>
      <td className="is-hidden-mobile">N/A</td>
    </tr>
  );
};

const BillingSubscriptions: React.FC = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const loadSubscriptions = (): void => {
    setIsLoading(true);

    customFetchV2<GetSubscriptionsResponse>("/api/billing/subscriptions", {
      method: "GET",
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("Ooops");
        }

        return response.data;
      })
      .then((data) => {
        setSubscriptions(data);
        setIsLoading(false);
      })
      .catch((error: Error) => {
        setErrorMsg(error.message);
        setIsLoading(false);
      });
  };

  useEffect(() => {
    loadSubscriptions();
  }, []);

  const renderSubscriptionRows = (subs: Subscription[]): React.JSX.Element[] =>
    subs.map((subscription, ix) => (
      <SubscriptionRow key={ix} subscription={subscription} />
    ));

  const renderContent = (): React.JSX.Element => {
    if (isLoading) {
      return <Loading />;
    }

    return (
      <table className="table is-striped is-hoverable is-fullwidth">
        <thead>
          <tr>
            <th>Device</th>
            <th>IMEI</th>
            <th className="is-hidden-mobile">Plan</th>
            <th className="is-hidden-mobile">Actions</th>
          </tr>
        </thead>
        <tbody>{renderSubscriptionRows(subscriptions)}</tbody>
      </table>
    );
  };

  const openActivateDevice = (): void => {
    // If running on mobile we have to call Cordova to open the URL in an external browser
    const url = 'https://shieldgps.co/activate';

    if (window.cordova) {
      cordova.InAppBrowser.open(url, '_system');
    } else {
      window.open('/activate', '_system');
    }
  };

  return (
    <div className="section">
      <SectionHeader
        title="Your Subscriptions"
        rightContent={<Button label="+" onClick={openActivateDevice} />}
      />
      {errorMsg && <div className="notification is-danger">{errorMsg}</div>}
      {renderContent()}
    </div>
  );
};

export { BillingSubscriptions };
